/**
 * @fileoverview Copilot Finops — Copilot Metrics Module
 * @description Displays cost estimation, premium request tracking, and monthly
 *              trends for Copilot usage across tracked users.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Collapse,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import Spinner from '@/components/common/Spinner/Spinner';
import {
  Users,
  Zap,
  RefreshCw,
  AlertCircle,
  BarChart3,
  DollarSign,
  CircleDollarSign,
  Coins,
  TrendingUp,
  ChevronDown,
} from 'lucide-react';
import { BarChart } from '@mui/x-charts/BarChart';
import UserBarChart from '@/features/GitHubUserMetrics/components/UserBarChart';
import UserDetailTable from '@/features/GitHubUserMetrics/components/UserDetailTable';
import MonthSelector from '@/features/GitHubUserMetrics/components/MonthSelector';
import { githubUserMetricsApi } from '@/services/api/githubUserMetrics';
import {
  getSummaryStats,
  getPremiumRequestsByUser,
  getCostByUser,
  getModelUsageAcrossUsers,
  getTokenUsageSummary,
} from '@/features/GitHubUserMetrics/utils';
import { appInsights } from '@/telemetry';
import '@/features/GitHubUserMetrics/pages/GitHubUserMetrics.css';
import './CopilotCost.css';

// ── Helpers ──────────────────────────────────────────────────

const formatCompactNumber = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return String(n);
};

const formatMonthLabel = (monthStr) => {
  try {
    const [y, m] = monthStr.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch { return monthStr; }
};

const formatMonthShortLabel = (monthStr) => {
  try {
    const [y, m] = monthStr.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'short' });
  } catch { return monthStr; }
};

const formatCurrencyAxis = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1000) {
    const thousands = n / 1000;
    return `$${thousands.toFixed(Number.isInteger(thousands) ? 0 : 1)}K`;
  }
  return `$${Math.round(n)}`;
};

// ── Sub-components ───────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
  <div className="gum-stat-card">
    <div className="gum-stat-icon" style={{ '--stat-color': color, '--stat-bg': bgColor }}>
      <Icon size={22} />
    </div>
    <div className="gum-stat-content">
      <div className="gum-stat-label">{label}</div>
      <div className="gum-stat-value" title={typeof value === 'number' ? value.toLocaleString() : String(value)}>
        {typeof value === 'number' ? formatCompactNumber(value) : value}
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, icon: Icon, badge, children }) => (
  <div className={`gum-chart-card${badge ? ' gum-chart-card--universal' : ''}`}>
    <div className="gum-chart-header">
      <h3 className="gum-chart-title">
        {Icon && <Icon size={18} />}
        {title}
      </h3>
      {badge && <span className="gum-chart-badge">{badge}</span>}
    </div>
    <div className="gum-chart-body">{children}</div>
  </div>
);

const CollapsibleChartRow = ({ title, defaultExpanded = false, children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div className={`gum-collapsible-row ${expanded ? 'expanded' : ''}`}>
      <div
        className="gum-collapsible-row-header"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}
        aria-expanded={expanded}
      >
        <span className="gum-collapsible-row-title">{title}</span>
        <ChevronDown size={18} className={`gum-chart-chevron ${expanded ? 'expanded' : ''}`} />
      </div>
      <Collapse in={expanded}>
        <div className="gum-chart-grid">{children}</div>
      </Collapse>
    </div>
  );
};

const ChartPlaceholder = ({ message = 'No data available' }) => (
  <div className="gum-chart-placeholder">
    <AlertCircle size={28} />
    <span>{message}</span>
  </div>
);

const SkeletonStat = () => <div className="gum-skeleton gum-skeleton-stat" />;
const SkeletonChart = () => <div className="gum-skeleton gum-skeleton-chart" />;

// ── Main Page Component ─────────────────────────────────────

export const CopilotCost = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [monthlyCostData, setMonthlyCostData] = useState(null);
  const [monthlyCostLoading, setMonthlyCostLoading] = useState(false);

  const loadMetrics = useCallback(async (force = false, month = selectedMonth) => {
    if (force) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const resp = await githubUserMetricsApi.getAllMetrics(force, month);
      const payload = resp?.data ?? resp;
      setMetricsData(payload);
      if (!month && payload?.month) setSelectedMonth(payload.month);
    } catch (err) {
      setError(err.message || 'Failed to load cost data');
      appInsights.trackException({ exception: err, properties: { source: 'CopilotCost', force, month } });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedMonth]);

  useEffect(() => { loadMetrics(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleMonthChange = useCallback((month) => {
    setSelectedMonth(month);
    loadMetrics(false, month);
  }, [loadMetrics]);

  const users = metricsData?.users ?? [];

  const tokenSummary = useMemo(() => getTokenUsageSummary(users), [users]);
  const premiumByUser = useMemo(() => getPremiumRequestsByUser(users, 15), [users]);
  const costByUser = useMemo(() => getCostByUser(users, 15), [users]);
  const modelUsage = useMemo(() => getModelUsageAcrossUsers(users), [users]);

  // ── Loading state ───────────────────────────────────────

  if (loading) {
    return (
      <Box className="gum-page finops-page">
        <Typography variant="h4" fontWeight={700} color="primary.main" mb={3}>
          <CircleDollarSign size={30} style={{ verticalAlign: 'middle', marginRight: 10 }} />
          AI Investment Tracking
        </Typography>
        <div className="gum-stats-row">
          {[1, 2, 3, 4].map((n) => <SkeletonStat key={n} />)}
        </div>
        <div className="gum-chart-grid">
          {[1, 2].map((n) => <SkeletonChart key={n} />)}
        </div>
      </Box>
    );
  }

  // ── Error state ─────────────────────────────────────────

  if (error && !metricsData) {
    return (
      <Box className="gum-page finops-page">
        <Typography variant="h4" fontWeight={700} color="primary.main" mb={3}>
          <CircleDollarSign size={30} style={{ verticalAlign: 'middle', marginRight: 10 }} />
          AI Investment Tracking
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // ── Main render ─────────────────────────────────────────

  return (
    <Box className="gum-page finops-page">
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary.main">
            <CircleDollarSign size={30} style={{ verticalAlign: 'middle', marginRight: 10 }} />
            AI Investment Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Premium request tracking and cost estimation for {metricsData?.org || 'Alaska-ITS'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          {activeTab === 1 && (
            <MonthSelector
              months={metricsData?.months || []}
              selected={selectedMonth}
              onChange={handleMonthChange}
              disabled={loading || refreshing}
            />
          )}
          <Button
            variant="outlined"
            startIcon={refreshing ? <Spinner size={16} /> : <RefreshCw size={16} />}
            onClick={() => loadMetrics(true, selectedMonth)}
            disabled={refreshing}
            size="small"
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      <Collapse in={!!error}>
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      </Collapse>

      {/* ── Tabs ─────────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Monthly Trends" icon={<TrendingUp size={16} />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Cost & Models" icon={<CircleDollarSign size={16} />} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {/* ── Tab 1: Monthly Trends ────────────────────────── */}
      {activeTab === 0 && (
        <MonthlyTrendsContent
          monthlyCostData={monthlyCostData}
          monthlyCostLoading={monthlyCostLoading}
          setMonthlyCostData={setMonthlyCostData}
          setMonthlyCostLoading={setMonthlyCostLoading}
        />
      )}

      {/* ── Tab 2: Cost & Models ─────────────────────────── */}
      {activeTab === 1 && (
        <>
          {tokenSummary.usersWithTokenData > 0 ? (
            <>
              <div className="gum-stats-row">
                <StatCard icon={Coins} label="Premium Requests" value={tokenSummary.totalPremiumRequests}
                  color="#f97316" bgColor="#fff7ed" />
                <StatCard icon={CircleDollarSign} label="Est. Cost (USD)" value={`$${tokenSummary.totalEstimatedCost.toFixed(2)}`}
                  color="#E31837" bgColor="#fef2f2" />
                <StatCard icon={Zap} label="CLI Tokens" value={tokenSummary.totalCliTokens}
                  color="#0097A9" bgColor="#ecfeff" />
                <StatCard icon={Users} label="Users w/ Token Data" value={tokenSummary.usersWithTokenData}
                  color="#6366f1" bgColor="#eef2ff" />
              </div>

              <CollapsibleChartRow title="Premium Requests & Cost by User" defaultExpanded={true}>
                <ChartCard title="Premium Requests (per User)" icon={Coins}>
                  <UserBarChart
                    data={premiumByUser}
                    bars={[{ key: 'premiumRequests', label: 'Premium Requests', color: '#f97316' }]}
                    maxItems={15}
                    emptyMessage="No premium request data available"
                  />
                </ChartCard>
                <ChartCard title="Estimated Cost (per User)" icon={CircleDollarSign}>
                  <UserBarChart
                    data={costByUser}
                    bars={[{ key: 'cost', label: 'Cost (USD)', color: '#E31837' }]}
                    valueFormatter={(v) => `$${v.toFixed(2)}`}
                    maxItems={15}
                    emptyMessage="No cost data available"
                  />
                </ChartCard>
              </CollapsibleChartRow>

              <CollapsibleChartRow title="Model Usage & Breakdown">
                <ChartCard title="Premium Requests by Model" icon={Zap}>
                  {modelUsage.length > 0 ? (
                    <BarChart
                      dataset={[
                        modelUsage.reduce((acc, d) => { acc[d.id] = d.value; return acc; }, { category: 'Models' }),
                      ]}
                      layout="horizontal"
                      xAxis={[{ label: 'Premium Requests', valueFormatter: formatCompactNumber }]}
                      yAxis={[{ scaleType: 'band', dataKey: 'category', tickLabelStyle: { fontSize: 11 } }]}
                      series={modelUsage.map((d, i) => ({
                        dataKey: d.id,
                        label: d.label,
                        stack: 'models',
                        color: ['#0097A9', '#00AF66', '#6366f1', '#f59e0b', '#ec4899', '#14b8a6', '#8b5cf6', '#3b82f6', '#ef4444', '#22c55e'][i % 10],
                        valueFormatter: (value) => formatCompactNumber(value),
                      }))}
                      height={150}
                      margin={{ left: 80, right: 20, bottom: 60, top: 30 }}
                      slotProps={{
                        legend: { direction: 'row', position: { vertical: 'bottom', horizontal: 'middle' }, itemMarkWidth: 10, itemMarkHeight: 10, labelStyle: { fontSize: 11 } },
                      }}
                    />
                  ) : (
                    <ChartPlaceholder message="No model usage data available" />
                  )}
                </ChartCard>
                <ChartCard title="Per-User Model Breakdown" icon={Users}>
                  <UserDetailTable users={users} />
                </ChartCard>
              </CollapsibleChartRow>
            </>
          ) : (
            <div className="gum-empty" style={{ marginTop: 24 }}>
              <Coins size={48} style={{ color: '#9ca3af', marginBottom: 12 }} />
              <Typography variant="h6" color="text.secondary">No cost data available</Typography>
              <Typography variant="body2" color="text.secondary">
                No token usage or premium request data found for the selected month.
              </Typography>
            </div>
          )}
        </>
      )}


    </Box>
  );
};

// ── Monthly Trends Content ──────────────────────────────────

const MonthlyTrendsContent = ({ monthlyCostData, monthlyCostLoading, setMonthlyCostData, setMonthlyCostLoading }) => {
  useEffect(() => {
    if (monthlyCostData) return;
    let cancelled = false;
    setMonthlyCostLoading(true);
    githubUserMetricsApi.getMonthlyCostSummary()
      .then((resp) => { if (!cancelled) setMonthlyCostData((resp?.data ?? resp)?.months ?? []); })
      .catch(() => { if (!cancelled) setMonthlyCostData([]); })
      .finally(() => { if (!cancelled) setMonthlyCostLoading(false); });
    return () => { cancelled = true; };
  }, [monthlyCostData, setMonthlyCostData, setMonthlyCostLoading]);

  if (monthlyCostLoading) {
    return (
      <div className="gum-chart-grid finops-monthly-chart-grid">
        <SkeletonChart />
        <SkeletonChart />
        <SkeletonChart />
      </div>
    );
  }

  if (!monthlyCostData || monthlyCostData.length === 0) {
    return (
      <div className="gum-empty" style={{ marginTop: 24 }}>
        <TrendingUp size={48} style={{ color: '#9ca3af', marginBottom: 12 }} />
        <Typography variant="h6" color="text.secondary">No monthly data available</Typography>
        <Typography variant="body2" color="text.secondary">
          Monthly cost data will appear once multiple months of usage data are collected.
        </Typography>
      </div>
    );
  }

  const ghcpCostData = monthlyCostData.filter((d) => d.month >= '2026-01');
  const azureFoundryCostData = [
    { month: 'Jan', costPerSeat: 151.07 },
    { month: 'Feb', costPerSeat: 154.89 },
    { month: 'Mar', costPerSeat: 156.17 },
    { month: 'Apr', costPerSeat: 166.60 },
    { month: 'May', costPerSeat: 158.16 },
    { month: 'Jun', costPerSeat: 158.71 },
    { month: 'Jul', costPerSeat: 134.64 },
  ];
  const licenseCostData = ghcpCostData.map((d) => ({ ...d, licenseCost: (d.totalTracked || d.users) * 39 }));

  return (
    <>
      <div className="gum-chart-grid finops-monthly-chart-grid">
        <ChartCard title="GHCP Premium Request Cost" icon={CircleDollarSign}>
          <BarChart
            dataset={ghcpCostData}
            xAxis={[{ scaleType: 'band', dataKey: 'month', valueFormatter: formatMonthShortLabel, tickLabelStyle: { fontSize: 9 } }]}
            yAxis={[{ valueFormatter: formatCurrencyAxis, tickLabelStyle: { fontSize: 9 } }]}
            series={[{ dataKey: 'estimatedCost', label: 'GHCP Premium Request Cost', color: '#01426A', valueFormatter: (v) => `$${v.toFixed(2)}` }]}
            height={260}
            margin={{ left: 38, right: 2, bottom: 28, top: 4 }}
            borderRadius={3}
            hideLegend
          />
        </ChartCard>

        <ChartCard title="Azure AI Foundry Cost" icon={CircleDollarSign}>
          <BarChart
            dataset={azureFoundryCostData}
            xAxis={[{ scaleType: 'band', dataKey: 'month', tickLabelStyle: { fontSize: 9 } }]}
            series={[{
              dataKey: 'costPerSeat',
              label: 'Azure AI Foundry Cost',
              color: '#0097A9',
              valueFormatter: (v) => `$${v.toFixed(2)}`,
            }]}
            yAxis={[{ valueFormatter: (v) => `$${v}`, tickLabelStyle: { fontSize: 9 } }]}
            height={260}
            margin={{ left: 36, right: 2, bottom: 28, top: 4 }}
            borderRadius={3}
            hideLegend
          />
        </ChartCard>
        <ChartCard title="GitHub Copilot License Cost" icon={CircleDollarSign}>
          <BarChart
            dataset={licenseCostData}
            xAxis={[{
              scaleType: 'band',
              dataKey: 'month',
              valueFormatter: formatMonthShortLabel,
              tickLabelStyle: { fontSize: 9, fontWeight: 500 },
            }]}
            series={[{
              dataKey: 'licenseCost',
              label: 'License Cost (Active Users × $39)',
              color: '#7c3aed',
              valueFormatter: (v) => `$${v.toLocaleString()} (${Math.round(v / 39)} licensed users × $39)`,
            }]}
            yAxis={[{ min: 0, max: 4000, tickMinStep: 1000, valueFormatter: (v) => `$${v / 1000}K`, tickLabelStyle: { fontSize: 9 } }]}
            height={260}
            margin={{ left: 38, right: 2, bottom: 28, top: 4 }}
            borderRadius={3}
            hideLegend
          />
        </ChartCard>
      </div>

      <ChartCard title="Monthly Summary" icon={BarChart3}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280' }}>Month</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280' }}>GHCP Premium Requests</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280' }}>GHCP Premium Cost</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280' }}>Azure AI Foundry Cost</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280' }}>License Cost</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280' }}>Total AI Spend</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const foundryData = { Jan: 151.07, Feb: 154.89, Mar: 156.17, Apr: 166.60, May: 158.16, Jun: 158.71, Jul: 134.64 };
                return [...monthlyCostData].filter((d) => d.month >= '2026-01').reverse().map((d) => {
                  const monthLabel = formatMonthLabel(d.month);
                  const shortMonth = monthLabel.split(' ')[0];
                  const foundryCost = foundryData[shortMonth] || 0;
                  const licenseCost = (d.totalTracked || d.users) * 39;
                  const totalSpend = d.estimatedCost + foundryCost + licenseCost;
                  return (
                    <tr key={d.month} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{monthLabel}</td>
                      <td style={{ textAlign: 'right', padding: '8px 12px' }}>{d.premiumRequests.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', padding: '8px 12px', color: '#01426A', fontWeight: 600 }}>${d.estimatedCost.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', padding: '8px 12px', color: '#0097A9', fontWeight: 600 }}>${foundryCost.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', padding: '8px 12px', color: '#7c3aed', fontWeight: 600 }}>${licenseCost.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: '#111827' }}>${totalSpend.toFixed(2)}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </>
  );
};

export default CopilotCost;
